import { ReactNode } from 'react';

import { IconCheckMS, IconCloseMS } from '../../../constants/icons';
import {
  BooleanChip,
  FieldLabel,
  FieldRoot,
  FieldValue,
  Grid,
  GroupBody,
  GroupGrid,
  GroupRoot,
  GroupTitle,
  LongText,
} from './styles';

const EMPTY = '—';
const YES = 'Sim';
const NO = 'Não';
const NOT_INFORMED = 'não informado';

interface FieldGridProps {
  children: ReactNode;
  /** Minimum column width; the grid fills the row with as many as fit. */
  minColumn?: number;
}

/**
 * Responsive label/value grid (guide 7.x): the building block of read-only
 * detail blocks. Empty values keep their label with "—" so nothing the bank
 * expects to see silently disappears.
 */
export function FieldGrid({
  children,
  minColumn = 220,
}: FieldGridProps): JSX.Element {
  return <Grid minColumn={minColumn}>{children}</Grid>;
}

interface FieldProps {
  label: string;
  value: string | null | undefined;
  /** Columns the field occupies in the grid. 99 = full row. */
  span?: number;
  /** Money and identifiers: stronger weight. */
  emphasis?: boolean;
  /** Tabular numerals. */
  mono?: boolean;
  children?: ReactNode;
}

export function Field({
  label,
  value,
  span,
  emphasis = false,
  mono = false,
  children,
}: FieldProps): JSX.Element {
  const isEmpty = value == null || value === '' || value === EMPTY;

  return (
    <FieldRoot span={span}>
      <FieldLabel>{label}</FieldLabel>
      {children ?? (
        <FieldValue
          isEmpty={isEmpty}
          emphasis={emphasis}
          mono={mono}
          aria-label={isEmpty ? `${label}: ${NOT_INFORMED}` : undefined}
        >
          {isEmpty ? EMPTY : value}
        </FieldValue>
      )}
    </FieldRoot>
  );
}

interface BooleanFieldProps {
  label: string;
  value: boolean;
  span?: number;
}

/** Flags become explicit Sim/Não chips. */
export function BooleanField({
  label,
  value,
  span,
}: BooleanFieldProps): JSX.Element {
  return (
    <FieldRoot span={span}>
      <FieldLabel>{label}</FieldLabel>
      <BooleanChip checked={value}>
        {value ? IconCheckMS : IconCloseMS}
        {value ? YES : NO}
      </BooleanChip>
    </FieldRoot>
  );
}

interface LongTextFieldProps {
  label: string;
  value: string | null | undefined;
}

/** Free text: full width, preserves line breaks. */
export function LongTextField({
  label,
  value,
}: LongTextFieldProps): JSX.Element {
  const isEmpty = !value || value.trim().length === 0;

  return (
    <FieldRoot span={99}>
      <FieldLabel>{label}</FieldLabel>
      <LongText isEmpty={isEmpty}>{isEmpty ? EMPTY : value}</LongText>
    </FieldRoot>
  );
}

interface FieldGroupProps {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}

/** Sub-block inside a section (e.g. "Valores", "Empresa de Avaliação"). */
export function FieldGroup({
  title,
  action,
  children,
}: FieldGroupProps): JSX.Element {
  return (
    <GroupRoot>
      <GroupTitle>
        <span>{title}</span>
        {action}
      </GroupTitle>
      <GroupBody>{children}</GroupBody>
    </GroupRoot>
  );
}

/** Lays several FieldGroups side by side, wrapping on narrow screens. */
export function FieldGroups({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  return <GroupGrid>{children}</GroupGrid>;
}
